import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { PROPERTY_TYPES, fmtDateTime } from "../lib/re";
import { Btn, Spinner } from "../components/re/ui";
import { toast } from "sonner";
import { Save, ShieldAlert } from "lucide-react";

function ChecklistEditor({ label, value, cats, onChange, testId }) {
  const sel = value || [];
  const toggle = (c) => onChange(sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c]);
  return (
    <div className="card-almar p-4" data-testid={testId}>
      <p className="text-sm font-semibold text-slate-100 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {cats.map((c) => (
          <button key={c} onClick={() => toggle(c)} className={`px-2.5 py-1 rounded-full text-xs border ${sel.includes(c) ? "bg-[#C9A84C]/20 border-[#C9A84C]/50 text-[#E0C574]" : "border-[#1E3A56] text-slate-400 hover:text-slate-200"}`}>{c}</button>
        ))}
      </div>
    </div>
  );
}

export default function Configuracoes() {
  const [s, setS] = useState(null);
  const [overType, setOverType] = useState("terreno");
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    api.get("/re/settings").then((r) => setS(r.data)).catch(() => toast.error("Erro ao carregar configurações"));
    api.get("/re/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);

  if (!s) return <Layout title="Configurações"><Spinner /></Layout>;
  const save = async () => {
    try {
      const r = await api.put("/re/settings", { max_upload_mb: Number(s.max_upload_mb), checklists: s.checklists, type_overrides: s.type_overrides, agency: s.agency || {} });
      setS(r.data); toast.success("Configurações guardadas");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao guardar"); }
  };
  const setOverride = (v) => {
    const o = { ...(s.type_overrides || {}) };
    if (v.length) o[overType] = v; else delete o[overType];
    setS({ ...s, type_overrides: o });
  };

  return (
    <Layout title="Configurações">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          <div className="card-almar p-5 flex flex-col sm:flex-row sm:items-end gap-4">
            <label className="flex-1"><span className="text-[11px] uppercase tracking-wider text-slate-400">Limite de tamanho por ficheiro (MB)</span>
              <input type="number" min={1} max={100} className="input-almar mt-1" value={s.max_upload_mb} onChange={(e) => setS({ ...s, max_upload_mb: e.target.value })} data-testid="settings-max-upload" /></label>
            <Btn onClick={save} data-testid="settings-save-btn"><Save size={15} /> Guardar configurações</Btn>
          </div>
          <h3 className="font-display text-lg text-[#C9A84C] pt-2">Dados da agência no site público</h3>
          <div className="card-almar p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="agency-settings">
            {[["name", "Nome"], ["phone", "Telefone"], ["email", "Email"], ["address", "Morada"], ["ami", "N.º Licença AMI"], ["instagram", "Instagram (URL)"], ["facebook", "Facebook (URL)"], ["linkedin", "LinkedIn (URL)"]].map(([k, l]) => (
              <label key={k}><span className="text-[11px] uppercase tracking-wider text-slate-400">{l}</span>
                <input className="input-almar mt-1" value={s.agency?.[k] || ""} placeholder={`[${l}]`} data-testid={`agency-${k}`} onChange={(e) => setS({ ...s, agency: { ...(s.agency || {}), [k]: e.target.value } })} /></label>
            ))}
            <p className="sm:col-span-2 text-[11px] text-slate-500">Campos vazios aparecem no site como marcadores para preencher.</p>
          </div>
          <h3 className="font-display text-lg text-[#C9A84C] pt-2">Checklist documental</h3>
          <ChecklistEditor label="Imóveis para venda" value={s.checklists?.venda} cats={s.doc_categories} testId="checklist-venda" onChange={(v) => setS({ ...s, checklists: { ...s.checklists, venda: v } })} />
          <ChecklistEditor label="Imóveis para arrendamento" value={s.checklists?.arrendamento} cats={s.doc_categories} testId="checklist-arrendamento" onChange={(v) => setS({ ...s, checklists: { ...s.checklists, arrendamento: v } })} />
          <div className="card-almar p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm font-semibold text-slate-100">Checklist específica por tipo de imóvel</span>
              <select className="input-almar py-1 text-sm w-auto" value={overType} onChange={(e) => setOverType(e.target.value)} data-testid="checklist-type-select">
                {Object.entries(PROPERTY_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">Se vazia, aplica-se a checklist da finalidade.</p>
            <ChecklistEditor label={PROPERTY_TYPES[overType]} value={s.type_overrides?.[overType]} cats={s.doc_categories} testId="checklist-override" onChange={setOverride} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="card-almar p-5" data-testid="activity-panel">
            <h3 className="font-display text-lg text-slate-100 flex items-center gap-2 mb-3"><ShieldAlert size={18} className="text-[#C9A84C]" /> Registo de atividade</h3>
            <div className="max-h-[480px] overflow-y-auto space-y-2">
              {activity.map((a) => (
                <div key={a.id} className="text-xs border-b border-[#1E3A56]/60 pb-2">
                  <p className="text-slate-200">{a.action}{a.details ? <span className="text-slate-400"> · {a.details}</span> : null}</p>
                  <p className="text-slate-500">{a.user_name} · {fmtDateTime(a.created_at)}</p>
                </div>
              ))}
              {activity.length === 0 && <p className="text-xs text-slate-500">Sem atividade.</p>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
